// Lib
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

// API
import { ORIGIN_URL } from '@api';

interface LinkPreviewData {
  fullName?: string;
  username?: string;
  tagline?: string;
  previewImageUrl?: string;
}

interface ProfileMainHelmetProps {
  linkPreviewData?: LinkPreviewData | null;
  domain?: string;
}

const ProfileMainHelmet: React.FC<ProfileMainHelmetProps> = ({
  linkPreviewData,
  domain,
}) => {
  const hostname =
    typeof window === 'object' ? window?.location?.hostname : domain;
  const url =
    typeof window === 'object'
      ? window?.location?.href
      : domain
      ? `https://${domain}`
      : '';
  const isMainDomain = ['dev.mrbl.bio', 'mrbl.bio', 'localhost']?.includes(
    hostname ?? '',
  );

  const author = useMemo(
    () => linkPreviewData?.fullName,

    [linkPreviewData],
  );

  const title = useMemo(() => {
    if (linkPreviewData) {
      return `Marble - ${author}`;
    }
  }, [linkPreviewData, author]);

  const tagline = useMemo(() => linkPreviewData?.tagline, [linkPreviewData]);

  const previewImage = useMemo(
    () =>
      linkPreviewData?.previewImageUrl
        ? linkPreviewData?.previewImageUrl
        : 'https://static.mrbl.bio/frontend/mixed/splashV3.jpg',
    [linkPreviewData],
  );

  return useMemo(
    () =>
      !linkPreviewData ? null : (
        <Helmet>
          <title>{title}</title>
          <meta
            httpEquiv="Cache-Control"
            content="no-cache, no-store, must-revalidate"
          />
          <meta httpEquiv="Pragma" content="no-cache" />
          <meta httpEquiv="Expires" content="0" />
          <meta property="og:type" content="profile" />
          <meta name="author" content={linkPreviewData?.username} />
          <meta
            property="og:url"
            content={
              isMainDomain
                ? `${ORIGIN_URL}/${linkPreviewData?.username}`
                : `${url}`
            }
          />
          <meta property="og:title" content={linkPreviewData?.username} />
          <meta property="twitter:title" content={title} />
          <meta property="description" content={tagline} />
          <meta property="og:description" content={tagline} />
          <meta property="twitter:description" content={tagline} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta property="og:image" content={previewImage} />
          <meta property="twitter:image" content={previewImage} />
        </Helmet>
      ),
    [linkPreviewData, title, previewImage, tagline, isMainDomain, url],
  );
};

export default ProfileMainHelmet;
